import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Flex, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  List,
  postAddComicToLists,
  postAddReleaseToLists,
  postRemoveComicFromList,
  postRemoveReleaseFromList,
} from "../../api/lists";
import { READING_LISTS_SHELF_QUERY_KEY } from "../../screens/shelf/hooks/useReadingListsShelf";
import { readingListDetailQueryKey } from "../../screens/shelf/ReadingListDetailScreen";
import { ConfirmAlertDialog } from "../common/ConfirmAlertDialog";
import { ReadingListContentPicker } from "./picker/ReadingListContentPicker";
import type { PickerItem, PickerListType } from "./picker/types";

export interface ReadingListAddComicPickerProps {
  list: List;
  isOpen: boolean;
  onClose: () => void;
}

export function ReadingListAddComicPicker({
  list,
  isOpen,
  onClose,
}: ReadingListAddComicPickerProps) {
  const [t] = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const isReleaseList = list.list_type === "releases";
  const listType: PickerListType = isReleaseList ? "releases" : "comics";

  // Snapshot of entity ids currently on the list — recomputed every render
  // from list.list_items so the baseline tracks server state.
  const baselineIds: Set<number> = useMemo(() => {
    const ids = new Set<number>();
    for (const item of list.list_items ?? []) {
      const id = item.context_entity_id ?? item.comic?.id ?? item.release?.id;
      if (typeof id === "number") ids.add(id);
    }
    return ids;
  }, [list.list_items]);

  // Pending intent for this editing session — not yet committed to server.
  const [pendingAdds, setPendingAdds] = useState<Map<number, PickerItem>>(
    new Map(),
  );
  const [pendingRemoves, setPendingRemoves] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [showDirtyConfirm, setShowDirtyConfirm] = useState(false);

  // Reset diff state every time the picker opens.
  useEffect(() => {
    if (isOpen) {
      setPendingAdds(new Map());
      setPendingRemoves(new Set());
    }
  }, [isOpen]);

  // What cards visually display as checked: baseline minus pending removes,
  // plus pending adds.
  const addedIds: Set<number> = useMemo(() => {
    const out = new Set<number>();
    baselineIds.forEach((id) => {
      if (!pendingRemoves.has(id)) out.add(id);
    });
    pendingAdds.forEach((_item, id) => out.add(id));
    return out;
  }, [baselineIds, pendingAdds, pendingRemoves]);

  const hasPending = pendingAdds.size > 0 || pendingRemoves.size > 0;

  const refreshListQueries = () => {
    queryClient.invalidateQueries({ queryKey: READING_LISTS_SHELF_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: readingListDetailQueryKey(list.id) });
  };

  const addMutation = useMutation({
    mutationFn: (entityId: number) =>
      isReleaseList
        ? postAddReleaseToLists(entityId, { list_ids: [list.id] })
        : postAddComicToLists(entityId, { list_ids: [list.id] }),
    onSuccess: () => refreshListQueries(),
  });

  const removeMutation = useMutation({
    mutationFn: (entityId: number) =>
      isReleaseList
        ? postRemoveReleaseFromList(entityId, list.id)
        : postRemoveComicFromList(entityId, list.id),
    onSuccess: () => refreshListQueries(),
  });

  const handleToggle = (item: PickerItem) => {
    if (pendingAdds.has(item.id)) {
      // User had queued this for add → cancel.
      setPendingAdds((prev) => {
        const next = new Map(prev);
        next.delete(item.id);
        return next;
      });
    } else if (pendingRemoves.has(item.id)) {
      // User had queued this for remove → cancel.
      setPendingRemoves((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    } else if (baselineIds.has(item.id)) {
      // Item is on the list, user wants to remove it.
      setPendingRemoves((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
    } else {
      // Item not on the list, user wants to add it.
      setPendingAdds((prev) => {
        const next = new Map(prev);
        next.set(item.id, item);
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (isSaving || !hasPending) return;
    setIsSaving(true);
    const addEntries = [...pendingAdds.entries()];
    const removeIds = [...pendingRemoves];

    const addResults = await Promise.allSettled(
      addEntries.map(([id]) => addMutation.mutateAsync(id)),
    );
    const removeResults = await Promise.allSettled(
      removeIds.map((id) => removeMutation.mutateAsync(id)),
    );

    // Re-build pending state with only the failures so retry doesn't re-fire
    // successes.
    const failedAdds = new Map<number, PickerItem>();
    addEntries.forEach(([id, item], i) => {
      if (addResults[i].status === "rejected") failedAdds.set(id, item);
    });
    const failedRemoves = new Set<number>();
    removeIds.forEach((id, i) => {
      if (removeResults[i].status === "rejected") failedRemoves.add(id);
    });

    setPendingAdds(failedAdds);
    setPendingRemoves(failedRemoves);
    setIsSaving(false);

    const totalFailed = failedAdds.size + failedRemoves.size;
    if (totalFailed === 0) {
      toast({
        title: t("screens.shelf.readingLists.picker.saveSuccess"),
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      onClose();
    } else {
      toast({
        title: t("screens.shelf.readingLists.picker.savePartialError", {
          count: totalFailed,
        }),
        status: "error",
        duration: 4500,
        isClosable: true,
      });
      // Picker stays open with only the failed items remaining in the diff
      // so the user can retry.
    }
  };

  const handleDiscard = () => {
    setPendingAdds(new Map());
    setPendingRemoves(new Set());
    onClose();
  };

  const handleRequestClose = () => {
    if (isSaving) return; // don't close mid-save
    if (hasPending) {
      setShowDirtyConfirm(true);
    } else {
      onClose();
    }
  };

  const handleDirtyDiscard = () => {
    setPendingAdds(new Map());
    setPendingRemoves(new Set());
    setShowDirtyConfirm(false);
    onClose();
  };

  const totalChanges = pendingAdds.size + pendingRemoves.size;

  const footer = (
    <Flex justify="flex-end" gap="8px">
      <Button
        onClick={handleDiscard}
        variant="ghost"
        color="dune.dune"
        _hover={{ bg: "transparent.white.10" }}
        isDisabled={isSaving || !hasPending}
      >
        {t("screens.shelf.readingLists.picker.discardButton")}
      </Button>
      <Button
        onClick={handleSave}
        bg="blaze.blaze"
        color="dune.dune"
        _hover={{ bg: "blaze.300" }}
        _active={{ bg: "blaze.300" }}
        isDisabled={!hasPending}
        isLoading={isSaving}
      >
        {hasPending
          ? t("screens.shelf.readingLists.picker.saveChanges", {
              count: totalChanges,
            })
          : t("screens.shelf.readingLists.picker.saveButton")}
      </Button>
    </Flex>
  );

  return (
    <>
      <ReadingListContentPicker
        isOpen={isOpen}
        onClose={handleRequestClose}
        listType={listType}
        addedIds={addedIds}
        onToggle={handleToggle}
        isPending={isSaving}
        footer={footer}
      />
      <ConfirmAlertDialog
        isOpen={showDirtyConfirm}
        onClose={() => setShowDirtyConfirm(false)}
        onConfirm={handleDirtyDiscard}
        title={t("screens.shelf.readingLists.picker.dirtyCloseTitle")}
        body={t("screens.shelf.readingLists.picker.dirtyCloseBody", {
          count: totalChanges,
        })}
        confirmLabel={t("screens.shelf.readingLists.picker.dirtyCloseConfirm")}
        cancelLabel={t("screens.shelf.readingLists.picker.dirtyCloseCancel")}
        isDestructive
      />
    </>
  );
}
